import React, { useState, useEffect } from 'react';
import { Table, Loader, Message, Button, Icon, Modal } from 'semantic-ui-react';
import { format } from 'date-fns';
import { getBids } from '~/api/projectBids';
import { Unit } from '~/constants';
import { normalizeToOverall, normalizeToPerWdc, normalizeToOverallStorage, normalizeToPerKwhStorage } from '~/utils/calculators/costsFns';
import { getProjectPvSizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { formatDollars, formatNumber, offtakeBidDecimals, offtakeRateUnit } from '~/utils';
import { ProjectBid } from '~/types/project';
import { usePermissions } from '~/requests/permissions/usePermissions';
import BidIrrForm from './BidIrrForm';

interface BidHistoryProps {
    project: any;
    investorId?: string; // Optional investor ID to filter bids by investor
}

interface Bid {
    id: string;
    amount: number;
    unit: string;
    bidStatus: string;
    calculatedIrr?: number;
    manualIrr?: number;
    irrSpreadsheetUrl?: string;
    manualIrrSpreadsheetUrl?: string | null;
    investorAcknowledgmentsFlag: boolean;
    createdAt: string;
    updatedAt: string;
    project?: {
        id: string;
        name: string;
    };
    investor?: {
        id: string;
        company?: {
            name: string;
        };
    };
    criteria?: Array<{
        type: string;
        data: any;
    }>;
}

const BidHistory: React.FC<BidHistoryProps> = ({ project, investorId }) => {
    const { permissions } = usePermissions();
    const [editingBidId, setEditingBidId] = useState<string | null>(null);
    const [bids, setBids] = useState<Bid[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [limit] = useState(10);
    const totalPvSizeKwdc = getProjectPvSizeKwdc(project);
    const totalStorageSizeKwh = getProjectStorageSizeKwh(project);
    const isStorageOnly = totalPvSizeKwdc === 0 && totalStorageSizeKwh > 0;

    const handleOpenEdit = (bidId: string, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setEditingBidId(bidId);
    };

    const handleCloseEdit = () => {
        setEditingBidId(null);
    };

    // Helper function to convert bid amounts between units
    const convertBidAmount = (bid: Bid, targetUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH') => {
        if (!bid.amount) return 0;
        const projectBid: ProjectBid = {
            amount: bid.amount,
            unit: bid.unit as keyof typeof Unit
        } as ProjectBid;

        if (isStorageOnly) {
            // For storage-only projects, assume bids are per KWH or total
            if (targetUnit === 'TOTAL') {

                return bid.unit === Unit.KWH
                    ? normalizeToOverallStorage(totalStorageSizeKwh, projectBid)
                    : bid.amount;
            } else {
                const projectBid: ProjectBid = {
                    amount: bid.amount,
                    unit: bid.unit as keyof typeof Unit
                } as ProjectBid;

                return bid.unit === Unit.KWH
                    ? bid.amount
                    : normalizeToPerKwhStorage(totalStorageSizeKwh, projectBid);
            }
        } else {
            if (!totalPvSizeKwdc) return 0;

            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.WDC
                    ? normalizeToOverall(totalPvSizeKwdc, projectBid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.WDC
                    ? bid.amount
                    : normalizeToPerWdc(totalPvSizeKwdc, projectBid);
            }
        }


    };

    // Helper function to format bid amounts for display
    const formatBidAmount = (bid: Bid, displayUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH') => {

        if (displayUnit === 'KWH') {
            const offtakeRate = bid.amount;
            return `$${formatNumber(offtakeRate, offtakeBidDecimals(project?.contractType))} /${offtakeRateUnit(project?.contractType)}`;
        }

        if (displayUnit === 'BESS_KWH') {
            return `$${formatNumber(bid.amount, 2)} /kWh`;
        }

        if (!totalPvSizeKwdc && !isStorageOnly) {
            return '-';
        }
        const convertedAmount = convertBidAmount(bid, displayUnit);
        const decimals = displayUnit === 'WDC' ? 3 : 0;
        return formatDollars(convertedAmount, { dec: decimals });
    };

    const fetchBids = async (filters: { investorId: string } = { investorId: '' }) => {
        try {
            setLoading(true);
            setError(null);

            const filterParams: any = {
                investorId: filters.investorId ? filters.investorId : undefined
            };

            const response = await getBids(project.id, filterParams);
            setBids(response);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch bids');
            setBids([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBids({
            investorId: investorId || ''
        });
    }, [project, limit]);

    const handleBidUpdate = (bidId: string, updatedData: any) => {
        setBids(prev => prev.map(bid =>
            bid.id === bidId ? { ...bid, ...updatedData } : bid
        ));
        handleCloseEdit();
    };

    const formatIRR = (calculatedIrr?: number, manualIrr?: number) => {
        const displayIrr = manualIrr ?? calculatedIrr;
        if (displayIrr == null) return '-';

        return (
            <span>
                {(displayIrr * 100).toFixed(2)}%
            </span>
        );
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'orange';
            case 'ACCEPTED':
                return 'green';
            case 'REJECTED':
                return 'red';
            default:
                return 'grey';
        }
    };

    if (loading && bids.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader active inline='centered' />
                <p>Loading bid history...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Message negative>
                <Message.Header>Error loading bid history</Message.Header>
                <p>{error}</p>
                <Button onClick={() => fetchBids()} primary>
                    Retry
                </Button>
            </Message>
        );
    }

    return (
        <div className="bid-history">
            {/* IRR Edit Modal */}
            <Modal
                open={!!editingBidId}
                onClose={handleCloseEdit}
                size="tiny"
            >
                <Modal.Header>Edit IRR</Modal.Header>
                <Modal.Content>
                    {editingBidId && (
                        <BidIrrForm
                            bid={bids.find(b => b.id === editingBidId)!}
                            projectId={project.id}
                            onSave={(updatedBid) => handleBidUpdate(editingBidId, updatedBid)}
                            onCancel={handleCloseEdit}
                        />
                    )}
                </Modal.Content>
            </Modal>

            {bids.length === 0 ? (
                <p>
                    No bids have been submitted.
                </p>
            ) : (
                <>
                    <Table compact>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                                {project.pricingType === 'OFFTAKE' ? (
                                    <Table.HeaderCell textAlign="right">Amount (per {offtakeRateUnit(project?.contractType)})</Table.HeaderCell>
                                ) : null}
                                {project.pricingType === 'ACQUISITION' || project.pricingType === 'DEV_FEE' ? (
                                    <React.Fragment>
                                        {isStorageOnly ? (
                                            <Table.HeaderCell textAlign="right">Amount ($/kWh)</Table.HeaderCell>
                                        ) : (
                                            <Table.HeaderCell textAlign="right">Amount ($/Wdc)</Table.HeaderCell>
                                        )}
                                        <Table.HeaderCell textAlign="right">Amount (Total)</Table.HeaderCell>
                                    </React.Fragment>
                                ) : null}
                                <Table.HeaderCell textAlign="right">IRR</Table.HeaderCell>

                                <Table.HeaderCell textAlign="right">Submitted</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {bids.map((bid) => (
                                <Table.Row key={bid.id}>
                                    <Table.Cell>
                                        <span
                                            style={{
                                                color: getStatusColor(bid.bidStatus),
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {bid.bidStatus}
                                        </span>
                                    </Table.Cell>
                                    {project.pricingType === 'OFFTAKE' ? (
                                        <Table.Cell textAlign="right">
                                            {formatBidAmount(bid, 'KWH')}
                                        </Table.Cell>
                                    ) : null}
                                    {project.pricingType === 'ACQUISITION' || project.pricingType === 'DEV_FEE' ? (
                                        <React.Fragment>
                                            {isStorageOnly ? (
                                                <Table.Cell textAlign="right">
                                                    {formatBidAmount(bid, 'BESS_KWH')}
                                                </Table.Cell>
                                            ) : (
                                                <Table.Cell textAlign="right">
                                                    {formatBidAmount(bid, 'WDC')}
                                                </Table.Cell>
                                            )}
                                            <Table.Cell textAlign="right">
                                                {formatBidAmount(bid, 'TOTAL')}
                                            </Table.Cell>
                                        </React.Fragment>
                                    ) : null}

                                    {permissions.isAdmin ? (
                                        <Table.Cell
                                            className="bid-history-clickable-irr-cell"
                                            onClick={(e: React.MouseEvent) => handleOpenEdit(bid.id, e)}
                                            textAlign="right"
                                        >
                                            <div className="bid-history-irr-display-container">
                                                {formatIRR(bid.calculatedIrr, bid.manualIrr)}
                                                <Icon
                                                    name="edit outline"
                                                    size="small"
                                                    className="bid-history-edit-icon"
                                                />
                                            </div>
                                        </Table.Cell>
                                    ) : null}

                                    <Table.Cell textAlign="right">
                                        {format(new Date(bid.createdAt), 'MMM d, yyyy h:mm a')}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>

                </>
            )}
        </div>
    );
};

export default BidHistory;
